import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { Drawer, Skeleton, Tooltip, Typography } from "antd";
import { LuInfo } from "react-icons/lu";
import MediaTrackingCard, { MediaTrackingCardSkeleton } from "./MediaTrackingCard";
import CustomEmpty from "../../commonComponents/CustomEmpty";
import { DATE_TIME_FORMATS } from "../../../utils/dateTimes";
import { convertUtcToLocal } from "../../../library/utils";
import { GET_LEAD_LINK_TRACKING, GET_LINK_TRACKING_DETAIL } from "../../../graphql/query";
import { LINK_TRACKING_NOTICE } from "./mediaTrackingHelpers";

function groupByDate(data) {
  const groups = {};
  data.forEach(item => {
    const dateKey = convertUtcToLocal(item?.createTs, DATE_TIME_FORMATS.MMM_DD_YYYY);
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(item);
  });
  return groups;
}

const MediaCountSummary = ({ mediaCount, loading }) => {
  if (loading) {
    return (
      <div className="flex gap-2 px-4 py-3">
        {[1, 2, 3].map(i => (
          <div
            key={i}
            className="flex-1 flex flex-col items-center bg-[#F5F5F5] py-2 px-4 rounded-md gap-1"
          >
            <Skeleton.Input active size="small" style={{ height: 24, width: 32, minWidth: 32 }} />
            <Skeleton.Input active size="small" style={{ height: 14, width: 60, minWidth: 60 }} />
          </div>
        ))}
      </div>
    );
  }

  const counts = mediaCount || {};
  const cells = [
    { value: Number(counts.image) || 0, label: "Image" },
    { value: Number(counts.video) || 0, label: "Videos" },
    { value: Number(counts.document) || 0, label: "Documents" },
  ];
  return (
    <div className="flex gap-2 px-4 py-3">
      {cells.map(cell => (
        <div
          key={cell.label}
          className="flex-1 flex flex-col items-center bg-[#F5F5F5] py-2 px-4 rounded-md"
        >
          <span className="text-black-88 text-xl font-bold">{cell.value}</span>
          <span className="text-black-65 text-xs font-normal">{cell.label}</span>
        </div>
      ))}
    </div>
  );
};

function MediaTrackingDrawer({ open, onClose, messageId, leadId }) {
  const [showAllMedia, setShowAllMedia] = useState(false);

  const [fetchLinkTrackingDetail, { data: detailData, loading: detailLoading }] = useLazyQuery(
    GET_LINK_TRACKING_DETAIL,
    { fetchPolicy: "network-only" }
  );

  const [fetchLeadLinkTracking, { data: listData, loading: listLoading }] = useLazyQuery(
    GET_LEAD_LINK_TRACKING,
    { fetchPolicy: "network-only" }
  );

  useEffect(() => {
    if (!open) return;
    setShowAllMedia(false);
    if (messageId != null) {
      fetchLinkTrackingDetail({ variables: { messageId: Number(messageId) } });
    }
  }, [open, messageId, fetchLinkTrackingDetail]);

  const handleShowAllMedia = () => {
    if (!showAllMedia) {
      setShowAllMedia(true);
      if (leadId != null) {
        fetchLeadLinkTracking({ variables: { leadId: Number(leadId) } });
      }
    } else {
      setShowAllMedia(false);
    }
  };

  const singleTracking = detailData?.getLinkTrackingDetail;
  const listTrackings = listData?.getLeadLinkTracking?.linkTrackings || [];
  const listMediaCount = listData?.getLeadLinkTracking?.mediaCount;

  const emptyState = (
    <CustomEmpty description="No media tracking data available." className="py-4" />
  );

  return (
    <Drawer
      title={
        <div className="flex items-center justify-between w-full">
          <span className="flex items-center gap-2">
            Media Tracking
            <Tooltip title={LINK_TRACKING_NOTICE} overlayStyle={{ maxWidth: 320 }}>
              <LuInfo className="text-black-45 cursor-help text-sm" />
            </Tooltip>
          </span>
          {leadId !== null && (
            <span className="text-sm text-blue-500 cursor-pointer" onClick={handleShowAllMedia}>
              {showAllMedia ? "Back" : "Show all media"}
            </span>
          )}
        </div>
      }
      width={460}
      closable
      onClose={onClose}
      open={open}
      styles={{ body: { padding: 0 } }}
    >
      <div className="flex flex-col">
        {showAllMedia ? (
          // "Show all media" view — list of all link trackings for the lead, grouped by date
          listLoading ? (
            // Loading skeletons while GET_LEAD_LINK_TRACKING is in flight
            <>
              <MediaCountSummary loading />
              {[1, 2, 3, 4, 5].map(i => (
                <MediaTrackingCardSkeleton key={i} showDetails={false} />
              ))}
            </>
          ) : listTrackings.length === 0 ? (
            // Empty state — lead has no media links sent
            emptyState
          ) : (
            // Per-type totals from API, then cards grouped by "sent at" date
            <>
              <MediaCountSummary mediaCount={listMediaCount} />
              {Object.entries(groupByDate(listTrackings)).map(([date, items]) => (
                <div key={date}>
                  <Typography.Text className="!text-xs !text-black-45 px-3 !font-semibold block text-start py-2">
                    {date}
                  </Typography.Text>
                  {items.map((item, index) => (
                    <MediaTrackingCard key={item.messageId ?? index} tracking={item} showMethod />
                  ))}
                </div>
              ))}
            </>
          )
        ) : detailLoading ? (
          // Default view — single-message detail from GET_LINK_TRACKING_DETAIL (loading)
          <MediaTrackingCardSkeleton />
        ) : !singleTracking ? (
          // Default view — API returned nothing for this message
          emptyState
        ) : (
          // Default view — one expanded card with this message's media tracking detail
          <MediaTrackingCard tracking={singleTracking} alwaysExpanded showMethod />
        )}
      </div>
    </Drawer>
  );
}

export default MediaTrackingDrawer;
